/**
 * Utility class for version comparison operations.
 * Ported as-is from src/app/core/utils/version-utils.ts.
 */
export class VersionUtils {

  /**
   * Compares two semantic version strings to determine if the current version
   * meets or exceeds the minimum required version
   *
   * @param currentVersion Current version string (e.g., "9.1.0", "9.0.0", "5.2.1")
   * @param minimumVersion Minimum required version string (e.g., "9.1.0")
   * @returns true if currentVersion >= minimumVersion, false otherwise
   *
   * @example
   * VersionUtils.isVersionSupported("9.1.0", "9.1.0") // returns true
   * VersionUtils.isVersionSupported("9.2.0", "9.1.0") // returns true
   * VersionUtils.isVersionSupported("9.0.0", "9.1.0") // returns false
   * VersionUtils.isVersionSupported("5.2.1", "9.1.0") // returns false
   */
  static isVersionSupported(currentVersion: string, minimumVersion: string): boolean {
    if (!currentVersion || !minimumVersion) {
      return false;
    }

    const parseVersion = (version: string): number[] => {
      return version.split('.').map(v => parseInt(v, 10));
    };

    const current = parseVersion(currentVersion);
    const minimum = parseVersion(minimumVersion);

    for (let i = 0; i < Math.max(current.length, minimum.length); i++) {
      const currentPart = current[i] || 0;
      const minimumPart = minimum[i] || 0;

      if (currentPart > minimumPart) return true;
      if (currentPart < minimumPart) return false;
    }

    return true; // Equal versions are supported
  }

  /**
   * Compares two semantic version strings
   *
   * @param version1 First version string
   * @param version2 Second version string
   * @returns -1 if version1 < version2, 0 if equal, 1 if version1 > version2
   *
   * @example
   * VersionUtils.compareVersions("9.1.0", "9.0.0") // returns 1
   * VersionUtils.compareVersions("9.0.0", "9.1.0") // returns -1
   * VersionUtils.compareVersions("9.1.0", "9.1.0") // returns 0
   */
  static compareVersions(version1: string, version2: string): number {
    if (!version1 || !version2) {
      throw new Error('Both version strings are required');
    }

    const parseVersion = (version: string): number[] => {
      return version.split('.').map(v => parseInt(v, 10));
    };

    const v1Parts = parseVersion(version1);
    const v2Parts = parseVersion(version2);

    for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
      const v1Part = v1Parts[i] || 0;
      const v2Part = v2Parts[i] || 0;

      if (v1Part > v2Part) return 1;
      if (v1Part < v2Part) return -1;
    }

    return 0; // Versions are equal
  }

  /**
   * Checks if vSAN ESA deduplication is supported for the given VCF version
   * vSAN ESA deduplication requires VCF version >= 9.1.0
   *
   * @param vcfVersion VCF version string
   * @returns true if ESA dedup is supported, false otherwise
   */
  static isVsanEsaDedupSupported(vcfVersion: string): boolean {
    return this.isVersionSupported(vcfVersion, '9.1.0');
  }

  /**
   * Checks if ReadyNode-based sizing is eligible for the given vSAN architecture/VCF version -
   * ESA only, and not VCF 5.2.1 (ReadyNode isn't available on 5.2.1 for either architecture).
   *
   * "No Preference" sizing means "let the sizing engine pick a ReadyNode for you", so it shares
   * this exact eligibility requirement - wherever ReadyNode-based sizing isn't supported, No
   * Preference isn't valid either.
   *
   * @param vsanArchitecture vSAN architecture string (e.g. "ESA", "OSA")
   * @param vcfVersion VCF version string (e.g. "9.1.0", "5.2.1")
   * @returns true if ReadyNode-based (and therefore No Preference) sizing is eligible
   */
  static isReadyNodeEligible(vsanArchitecture: string | undefined | null, vcfVersion: string | undefined | null): boolean {
    const isEsa = !!vsanArchitecture && vsanArchitecture.toString().trim().toUpperCase() === 'ESA';
    const version = (vcfVersion ?? '').toString().trim();
    const isVersion521 = version === '5.2.1' || version.startsWith('5.2.1');
    return isEsa && !isVersion521;
  }

  /**
   * Checks if vSAN Max (vSAN Disaggregated Storage Clusters) is supported for the given VCF version.
   * vSAN Max requires VCF version >= 9.0.0.
   *
   * @param vcfVersion VCF version string
   * @returns true if vSAN Max is supported, false otherwise
   */
  static isVsanMaxSupported(vcfVersion: string | undefined | null): boolean {
    return this.isVersionSupported((vcfVersion ?? '').toString().trim(), '9.0.0');
  }
}
